'use client'

import { motion, AnimatePresence } from 'framer-motion'
import { AlertTriangle, X } from 'lucide-react'

interface ConfirmModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    title: string
    message: string
    confirmText?: string
    cancelText?: string
    type?: 'warning' | 'danger' | 'info'
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
                                                       isOpen,
                                                       onClose,
                                                       onConfirm,
                                                       title,
                                                       message,
                                                       confirmText = 'Confirmer',
                                                       cancelText = 'Annuler',
                                                       type = 'warning'
                                                   }) => {
    const handleConfirm = () => {
        onConfirm()
        onClose()
    }

    const colors = {
        warning: {
            icon: 'text-amber-600',
            bg: 'from-amber-50 to-orange-50',
            border: 'border-amber-200',
            button: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500'
        },
        danger: {
            icon: 'text-red-600',
            bg: 'from-red-50 to-pink-50',
            border: 'border-red-200',
            button: 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
        },
        info: {
            icon: 'text-bordeaux-600',
            bg: 'from-bordeaux-50 to-gold-50',
            border: 'border-bordeaux-200',
            button: 'bg-bordeaux-600 hover:bg-bordeaux-700 focus:ring-bordeaux-500'
        }
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    onClick={onClose}
                >
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ type: 'spring', duration: 0.3 }}
                        className={`relative bg-gradient-to-br ${colors[type].bg} rounded-2xl shadow-2xl border-2 ${colors[type].border} max-w-md w-full overflow-hidden`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="p-6 pb-4">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center">
                                    <div className={`w-12 h-12 rounded-full bg-white shadow-inner flex items-center justify-center mr-4`}>
                                        <AlertTriangle className={`w-6 h-6 ${colors[type].icon}`} />
                                    </div>
                                    <h3 className="text-xl font-bold text-bordeaux-700">
                                        {title}
                                    </h3>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="p-2 hover:bg-white/50 rounded-full transition-colors"
                                >
                                    <X className="w-5 h-5 text-bordeaux-500" />
                                </button>
                            </div>

                            <p className="text-bordeaux-600 leading-relaxed">
                                {message}
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="px-6 pb-6">
                            <div className="flex space-x-3">
                                <button
                                    onClick={onClose}
                                    className="flex-1 px-4 py-3 bg-white border-2 border-bordeaux-200 text-bordeaux-700 font-semibold rounded-xl hover:bg-bordeaux-50 hover:border-bordeaux-300 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-bordeaux-200"
                                >
                                    {cancelText}
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    className={`flex-1 px-4 py-3 ${colors[type].button} text-white font-semibold rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-opacity-50 shadow-lg hover:shadow-xl`}
                                >
                                    {confirmText}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export default ConfirmModal
